import {
  ActionMenu,
  ActionMenuContent,
  ActionMenuItem,
  ActionMenuTrigger,
} from './action-menu';
import { MenuChevronDownIcon, MenuLocationIcon } from './menu-icons';
import { FilterClearButton } from './filters/filter-clear-button';
import { cn } from '@noor/lib/cn';

/** One selectable branch. */
export interface BranchOption {
  id: string;
  name: string;
}

interface BranchSelectorProps {
  /** Label of the active branch. */
  branchName: string;
  ariaLabel: string;
  /** Branches to choose from. A single-branch merchant renders a plain pill. */
  branches?: BranchOption[] | undefined;
  activeBranchId?: string | undefined;
  onSelect?: ((id: string) => void) | undefined;
  /**
   * Label for an "all branches" entry above the branch list («Все филиалы»).
   * Omitted, the menu offers branches only — the screens that are strictly
   * branch-scoped (Menu, Promotions) have no merchant-wide list to widen to.
   */
  allLabel?: string | undefined;
  /** True while "all branches" is the current selection. */
  allSelected?: boolean | undefined;
  /** Picks the "all branches" entry. Required for `allLabel` to render. */
  onSelectAll?: (() => void) | undefined;
}

const TRIGGER =
  'flex h-10 w-full min-w-0 items-center gap-2 rounded-full border border-input bg-card px-4 text-left tracking-tight shadow-xs transition-[color,box-shadow] hover:bg-muted/40 focus-visible:border-input-border-focus focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none sm:w-75';

/**
 * The branch picker — a filled input-style pill with a leading location pin and
 * a trailing chevron, opening a menu of the merchant's branches. It's a button
 * (not a text field) because it selects from a list rather than accepting free
 * text. With nothing to pick — one branch and no "all" entry — it renders as a
 * static pill instead of an interactive menu.
 */
export function BranchSelector({
  branchName,
  ariaLabel,
  branches,
  activeBranchId,
  onSelect,
  allLabel,
  allSelected,
  onSelectAll,
}: BranchSelectorProps) {
  const options = branches ?? [];
  const showAll = allLabel !== undefined && onSelectAll !== undefined;
  // "All" is a second thing to pick, so it makes even a single-branch merchant's
  // control interactive — a merchant with one branch can still be shown their
  // whole-merchant list, and the two options are genuinely different requests.
  const isPickable =
    (options.length > 1 && onSelect !== undefined) || (showAll && options.length > 0);

  // A picked branch is clearable back to "all", like every `Filters.Select` —
  // the trigger wears the same × in place of the chevron. Shown only while a
  // specific branch is picked: with "all" active there is nothing to clear.
  const clearable = showAll && !allSelected;

  const label = (
    <>
      <MenuLocationIcon className="size-5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-base text-foreground">
        {allSelected && showAll ? allLabel : branchName}
      </span>
      <MenuChevronDownIcon
        className={cn(
          'size-5 shrink-0 text-muted-foreground',
          !isPickable && 'opacity-0',
        )}
      />
    </>
  );

  if (!isPickable) {
    return (
      <div className={TRIGGER} aria-label={ariaLabel}>
        {label}
      </div>
    );
  }

  // `ActionMenu`, NOT the generic `DropdownMenu`: this is a FILTER popup, and
  // every filter popup in the panel wears the one signature card shape — 16px
  // radius, 4px padding, no border, soft ambient shadow, 12px-radius rows. The
  // generic dropdown draws a bordered 6px-radius card with tighter rows, which
  // put a visibly different popup next to the status selects on the same screen.
  return (
    <div className="relative w-full sm:w-75">
      <ActionMenu>
        <ActionMenuTrigger
          className={cn(
            TRIGGER,
            // Make room for the clear button + hide the chevron when a branch
            // is picked, so the "×" takes the chevron's place — same treatment
            // as `Filters.Select`.
            clearable && 'pr-9 [&>svg:last-child]:hidden',
          )}
          aria-label={ariaLabel}
        >
          {label}
        </ActionMenuTrigger>
        <ActionMenuContent align="start" className="w-75">
          {showAll ? (
            <ActionMenuItem
              onSelect={onSelectAll}
              className={cn(allSelected && 'font-semibold')}
            >
              {allLabel}
            </ActionMenuItem>
          ) : null}
          {options.map((branch) => (
            <ActionMenuItem
              key={branch.id}
              onSelect={() => onSelect?.(branch.id)}
              className={cn(
                !allSelected && branch.id === activeBranchId && 'font-semibold',
              )}
            >
              {branch.name}
            </ActionMenuItem>
          ))}
        </ActionMenuContent>
      </ActionMenu>
      {clearable ? (
        <FilterClearButton
          onClear={() => onSelectAll?.()}
          aria-label={`${ariaLabel} — clear`}
          className="absolute top-1/2 right-3 -translate-y-1/2"
        />
      ) : null}
    </div>
  );
}
